import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { ProjectBasedLearningCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const ProblemTypeStep: React.FC<
  StepComponentProps<ProjectBasedLearningCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomProblemType, setShowCustomProblemType] = useState(false);

  const problemTypeOptions = [
    {
      id: "Gerçek Dünya Problemi",
      emoji: "🌍",
      label: t("projectBasedLearning.problemType.realWorld"),
      desc: t("projectBasedLearning.problemType.realWorldDesc"),
    },
    {
      id: "Araştırma Odaklı Problem",
      emoji: "🔬",
      label: t("projectBasedLearning.problemType.research"),
      desc: t("projectBasedLearning.problemType.researchDesc"),
    },
    {
      id: "Yaratıcı Tasarım Problemi",
      emoji: "🎨",
      label: t("projectBasedLearning.problemType.creative"),
      desc: t("projectBasedLearning.problemType.creativeDesc"),
    },
    {
      id: "Sosyal Sorun Çözme",
      emoji: "🤝",
      label: t("projectBasedLearning.problemType.social"),
      desc: t("projectBasedLearning.problemType.socialDesc"),
    },
    {
      id: "Teknoloji Geliştirme",
      emoji: "💻",
      label: t("projectBasedLearning.problemType.technology"),
      desc: t("projectBasedLearning.problemType.technologyDesc"),
    },
    {
      id: "Çevresel Problem",
      emoji: "🌱",
      label: t("projectBasedLearning.problemType.environmental"),
      desc: t("projectBasedLearning.problemType.environmentalDesc"),
    },
  ];

  const handleProblemTypeChange = (problemTypeId: string) => {
    onDataChange({ problemType: problemTypeId });
  };

  const handleCustomProblemTypeToggle = () => {
    const hasCustomProblemType =
      data.problemType &&
      !problemTypeOptions.some((option) => option.id === data.problemType);

    if (hasCustomProblemType || showCustomProblemType) {
      onDataChange({ problemType: "" });
      setShowCustomProblemType(false);
    } else {
      setShowCustomProblemType(true);
    }
  };

  const handleCustomProblemTypeChange = (value: string) => {
    onDataChange({ problemType: value.trim() });
  };

  const isProblemTypeSelected = (problemTypeId: string) => {
    return data.problemType === problemTypeId;
  };

  const isCustomProblemTypeSelected = () => {
    return (
      showCustomProblemType ||
      (data.problemType &&
        !problemTypeOptions.some((option) => option.id === data.problemType))
    );
  };

  const getCustomProblemTypeValue = () => {
    if (
      data.problemType &&
      !problemTypeOptions.some((option) => option.id === data.problemType)
    ) {
      return data.problemType;
    }
    return "";
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("projectBasedLearning.steps.problemType.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("projectBasedLearning.steps.problemType.description")}
        </p>
      </div>

      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("projectBasedLearning.form.problemType")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("projectBasedLearning.form.problemTypeDesc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {problemTypeOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isProblemTypeSelected(option.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleProblemTypeChange(option.id)}
            >
              <div className="flex items-start gap-3">
                <div>
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                      isProblemTypeSelected(option.id)
                        ? `border-${themeColor}-500 bg-${themeColor}-500`
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                  >
                    {isProblemTypeSelected(option.id) && (
                      <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Problem Type Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomProblemTypeSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomProblemTypeToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  isCustomProblemTypeSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomProblemTypeSelected() && (
                  <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("projectBasedLearning.problemType.other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("projectBasedLearning.problemType.otherDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Problem Type Input */}
        {isCustomProblemTypeSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("projectBasedLearning.form.customProblemType")}
            </label>
            <input
              type="text"
              value={getCustomProblemTypeValue()}
              onChange={(e) => handleCustomProblemTypeChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t(
                "projectBasedLearning.form.customProblemTypePlaceholder",
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};
